import type { TrailerBodyStyle, TrailerHitch, TruckBodyStyle, TruckCoupling } from '@chukua/common';

/** What each part is drawn as: the vehicle's own paint, or a brand colour for the parts every vehicle shares. */
export type SilhouetteRole = 'paint' | 'chassis' | 'tyre' | 'hub' | 'window' | 'container' | 'tipper' | 'dropside' | 'gap';

export type SilhouetteShape =
  | { kind: 'rect'; role: SilhouetteRole; x: number; y: number; width: number; height: number; paint?: 'truck' | 'trailer' }
  | { kind: 'polygon'; role: SilhouetteRole; points: [number, number][]; paint?: 'truck' | 'trailer' }
  | { kind: 'circle'; role: SilhouetteRole; cx: number; cy: number; r: number };

export interface SilhouetteTruck {
  bodyStyle: TruckBodyStyle | null;
  coupling: TruckCoupling | null;
}

export interface SilhouetteTrailer {
  bodyStyle: TrailerBodyStyle | null;
  hitch: TrailerHitch | null;
}

export interface Silhouette {
  width: number;
  height: number;
  shapes: SilhouetteShape[];
}

/** Units of the drawing: one is roughly 15 cm, so a 40ft skeletal is 124 long. Front is on the right, as the plan chart has it. */
const GROUND = 44;
const TRUCK_LENGTH: Record<TruckBodyStyle, number> = { tractor: 62, tipper: 84, dropside: 74, box: 62, tanker: 72 };
const TRAILER_LENGTH: Record<TrailerBodyStyle, number> = {
  skeletal_40: 124,
  skeletal_20: 72,
  flatbed: 118,
  sidewall: 118,
  tanker: 116,
  lowbed: 118,
  reefer: 118,
  tipper: 104,
  drawbar: 76,
};
/** How far a semi-trailer's nose reaches over the tractor's fifth wheel. */
const KINGPIN_OVERLAP = 26;
const DRAWBAR_GAP = 10;

const rect = (role: SilhouetteRole, x: number, y: number, width: number, height: number, paint?: 'truck' | 'trailer'): SilhouetteShape => ({
  kind: 'rect',
  role,
  x,
  y,
  width,
  height,
  paint,
});

function wheel(cx: number, r = 5): SilhouetteShape[] {
  return [
    { kind: 'circle', role: 'tyre', cx, cy: GROUND - r, r },
    { kind: 'circle', role: 'hub', cx, cy: GROUND - r, r: r * 0.42 },
  ];
}

function cab(x: number): SilhouetteShape[] {
  return [rect('paint', x, 8, 21, 24, 'truck'), rect('window', x + 12, 11, 8, 8), rect('chassis', x + 19, 29, 4, 5)];
}

function container(x: number, width: number): SilhouetteShape[] {
  return [rect('container', x, 6, width, 23)];
}

function truckBody(style: TruckBodyStyle, x: number, length: number): SilhouetteShape[] {
  const cabX = x + length - 22;
  switch (style) {
    case 'tractor':
      return [rect('chassis', x + 9, 28, 15, 3), ...cab(cabX), ...wheel(x + length - 11), ...wheel(x + 12), ...wheel(x + 23)];
    case 'tipper':
      return [
        {
          kind: 'polygon',
          role: 'tipper',
          points: [
            [x + 3, 11],
            [x + 60, 11],
            [x + 60, 31],
            [x + 9, 31],
          ],
        },
        ...cab(cabX),
        ...wheel(x + length - 11),
        ...wheel(x + length - 24),
        ...wheel(x + 12),
        ...wheel(x + 23),
      ];
    case 'dropside':
      return [rect('dropside', x + 3, 21, length - 27, 10), ...cab(cabX), ...wheel(x + length - 11), ...wheel(x + 12), ...wheel(x + 23)];
    case 'tanker':
      return [rect('paint', x + 3, 12, length - 27, 19, 'truck'), ...cab(cabX), ...wheel(x + length - 11), ...wheel(x + 12), ...wheel(x + 23)];
    case 'box':
      return [rect('paint', x + 3, 5, length - 26, 26, 'truck'), ...cab(cabX), ...wheel(x + length - 11), ...wheel(x + 15)];
  }
}

function drawTruck(truck: SilhouetteTruck, x: number): { shapes: SilhouetteShape[]; length: number } {
  const style = truck.bodyStyle ?? 'tractor';
  const length = TRUCK_LENGTH[style];
  return { shapes: [rect('chassis', x + 2, 31, length - 4, 4), ...truckBody(style, x, length)], length };
}

function triAxle(x: number): SilhouetteShape[] {
  return [...wheel(x + 12), ...wheel(x + 23), ...wheel(x + 34)];
}

/** A lowbed: a gooseneck up front over a low deck on small wheels. */
function lowbed(x: number, length: number): SilhouetteShape[] {
  return [
    {
      kind: 'polygon',
      role: 'paint',
      paint: 'trailer',
      points: [
        [x, 31],
        [x + 84, 31],
        [x + 90, 24],
        [x + length, 24],
        [x + length, 28],
        [x + 92, 28],
        [x + 86, 35],
        [x, 35],
      ],
    },
    ...wheel(x + 9, 3.6),
    ...wheel(x + 17, 3.6),
    ...wheel(x + 25, 3.6),
  ];
}

/** A drawbar trailer: its own box on three axles, and the bar it is towed by. */
function drawbar(x: number, length: number): SilhouetteShape[] {
  return [
    rect('paint', x, 15, length - 4, 13, 'trailer'),
    rect('chassis', x, 28, length - 4, 3),
    rect('chassis', x + length - 4, 32, DRAWBAR_GAP + 4, 1.6),
    ...wheel(x + 12),
    ...wheel(x + 23),
    ...wheel(x + length - 16),
  ];
}

function trailerBody(style: TrailerBodyStyle, x: number, length: number): SilhouetteShape[] {
  const frame = rect('chassis', x, 29, length, 3);
  switch (style) {
    case 'skeletal_40':
      return [frame, ...container(x + 2, length - 4), ...triAxle(x)];
    case 'skeletal_20':
      return [frame, ...container(x + 6, length - 12), ...wheel(x + 12), ...wheel(x + 23)];
    case 'flatbed':
      return [rect('paint', x, 26, length, 5, 'trailer'), ...triAxle(x)];
    case 'sidewall':
      return [rect('paint', x, 17, length, 14, 'trailer'), ...triAxle(x)];
    case 'tanker':
      return [frame, rect('paint', x + 2, 9, length - 4, 20, 'trailer'), ...triAxle(x)];
    case 'reefer':
      return [rect('paint', x, 5, length - 4, 26, 'trailer'), rect('chassis', x + length - 4, 9, 4, 13), ...triAxle(x)];
    case 'tipper':
      return [
        frame,
        {
          kind: 'polygon',
          role: 'tipper',
          points: [
            [x + 2, 10],
            [x + length - 2, 10],
            [x + length - 8, 29],
            [x + 6, 29],
          ],
        },
        ...triAxle(x),
      ];
    case 'lowbed':
      return lowbed(x, length);
    case 'drawbar':
      return drawbar(x, length);
  }
}

function drawTrailer(trailer: SilhouetteTrailer, x: number, parked: boolean): { shapes: SilhouetteShape[]; length: number } {
  const style = trailer.bodyStyle ?? 'skeletal_40';
  const length = TRAILER_LENGTH[style];
  const shapes = trailerBody(style, x, length);
  // A parked semi-trailer stands on its landing legs.
  if (parked && trailer.hitch !== 'drawbar') shapes.push(rect('chassis', x + length - 30, 32, 2, 12));
  return { shapes, length };
}

/**
 * A side view of a truck, a trailer, or the two coupled (gitaumoses4/chukua#390), as shapes for an SVG. A semi-trailer's nose
 * sits over the tractor's fifth wheel; a drawbar trailer trails its truck on a bar. A truck that can tow but has no trailer gets
 * a dashed outline where one would go, so an empty fifth wheel reads as "free", not as a rigid truck.
 */
export function rigSilhouette({ truck, trailer }: { truck: SilhouetteTruck | null; trailer: SilhouetteTrailer | null }): Silhouette {
  const shapes: SilhouetteShape[] = [];
  let x = 2;
  if (trailer) {
    const drawn = drawTrailer(trailer, x, truck === null);
    shapes.push(...drawn.shapes);
    x += truck ? drawn.length + (trailer.hitch === 'drawbar' ? DRAWBAR_GAP : -KINGPIN_OVERLAP) : drawn.length;
  } else if (truck && truck.coupling && truck.coupling !== 'none') {
    const slot = truck.coupling === 'drawbar_hitch' ? 46 : 70;
    shapes.push(rect('gap', 1, 10, slot, 24));
    x += slot;
  }
  if (truck) {
    const drawn = drawTruck(truck, x);
    shapes.push(...drawn.shapes);
    x += drawn.length;
  }
  return { width: x + 2, height: 46, shapes };
}
